import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowRightLeft, Search, CalendarClock, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { migrationV1V2Api } from './api/migration-v1-v2';
import { BoardColumn } from './components/BoardColumn';
import { AssignUserControl } from './components/AssignUserControl';
import { PlannedDateControl } from './components/PlannedDateControl';
import { isoOf, todayIso } from './utils/date';
import { useAssignableUsers } from './hooks/useAssignableUsers';
import type { BoardCard, BoardData, BoardColumnKey } from './types';

type PlanMode = '' | 'day' | 'week';

/**
 * Convertit (mode, date) en plage inclusive [from, to] pour l'API :
 *  - jour   → from = to = la date choisie
 *  - semaine → lundi..dimanche de la semaine contenant la date choisie
 */
function planRange(mode: PlanMode, date: string): { from?: string; to?: string } {
  if (!mode || !date) return {};
  if (mode === 'day') return { from: date, to: date };
  const [y, m, d] = date.split('-').map(Number);
  const base = new Date(y, (m ?? 1) - 1, d ?? 1);
  const dow = (base.getDay() + 6) % 7; // 0 = lundi
  const monday = new Date(base);
  monday.setDate(base.getDate() - dow);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { from: isoOf(monday), to: isoOf(sunday) };
}

const COLUMN_DEFS: { key: BoardColumnKey; title: string }[] = [
  { key: 'todo', title: 'À faire' },
  { key: 'inProgress', title: 'Migration en cours' },
  { key: 'blocked', title: 'Migration bloquée' },
  { key: 'completed', title: 'Migration terminée' },
];

interface HeaderStatProps {
  label: string;
  value: number;
  color: string;
}

function HeaderStat({ label, value, color }: HeaderStatProps) {
  return (
    <div className="px-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800/50">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

export function BoardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, hasPermission } = useAuth();
  const canManage = hasPermission('action.migration_v1_v2.manage');
  const assignableUsers = useAssignableUsers(canManage);

  const [board, setBoard] = useState<BoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [selfHasNoProjects, setSelfHasNoProjects] = useState(false);

  // Sans ownerId explicite dans l'URL, l'utilisateur est filtré par défaut
  // sur ses propres dossiers ; repli sur « Tous » s'il n'en a aucun.
  const rawOwnerId = searchParams.get('ownerId');
  const defaultOwnerId = user && !selfHasNoProjects ? String(user.id) : 'all';
  const ownerIdParam = rawOwnerId || defaultOwnerId;
  const searchParam = searchParams.get('search') || '';
  const planMode = (searchParams.get('planMode') as PlanMode) || '';
  const planDate = searchParams.get('planDate') || '';

  const { from: plannedFrom, to: plannedTo } = useMemo(
    () => planRange(planMode, planDate),
    [planMode, planDate]
  );

  const updateParams = useCallback(
    (next: { search?: string; ownerId?: string; planMode?: PlanMode; planDate?: string }) => {
      const params = new URLSearchParams();
      const search = next.search !== undefined ? next.search : searchParam;
      const ownerId = next.ownerId !== undefined ? next.ownerId : rawOwnerId;
      const mode = next.planMode !== undefined ? next.planMode : planMode;
      const date = next.planDate !== undefined ? next.planDate : planDate;
      if (search) params.set('search', search);
      // Un choix explicite (y compris « Tous ») est conservé dans l'URL pour
      // qu'il prime sur le filtre par défaut.
      if (ownerId) params.set('ownerId', ownerId);
      if (mode) {
        params.set('planMode', mode);
        // Défaut : aujourd'hui dès qu'un mode de filtre par date est activé.
        params.set('planDate', date || todayIso());
      }
      setSearchParams(params);
    },
    [searchParam, rawOwnerId, planMode, planDate, setSearchParams]
  );

  const hasActiveFilters = !!searchParam || !!rawOwnerId || !!planMode;

  const resetFilters = useCallback(() => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== searchParam) {
        updateParams({ search: searchInput });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, searchParam, updateParams]);

  const fetchBoard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await migrationV1V2Api.getBoard({
        search: searchParam || undefined,
        ownerId: ownerIdParam === 'all' ? 'all' : Number(ownerIdParam),
        plannedFrom,
        plannedTo,
      });
      // Filtre par défaut sur soi-même mais aucun dossier assigné : basculer
      // sur « Tous » (la liste owners renvoyée n'est pas filtrée).
      if (!rawOwnerId && user && ownerIdParam === String(user.id) && !data.owners.some((o) => o.id === user.id)) {
        setSelfHasNoProjects(true);
        return;
      }
      setBoard(data);
    } catch {
      setError('Erreur lors du chargement du board');
    } finally {
      setLoading(false);
    }
  }, [searchParam, ownerIdParam, rawOwnerId, user, plannedFrom, plannedTo]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  // Ouverture du dossier : admins (manage) partout ; sinon uniquement le
  // responsable assigné. Une carte sans projet nécessite manage (l'ouverture
  // crée le projet).
  const canOpenCard = useCallback(
    (card: BoardCard) =>
      canManage || (!!card.project && card.project.assignedTo?.id === user?.id),
    [canManage, user?.id]
  );

  const onCardClick = useCallback(
    async (card: BoardCard) => {
      if (card.project) {
        navigate(`/migration-v1-v2/${card.project.id}`);
        return;
      }
      try {
        const project = await migrationV1V2Api.createProject({ schoolId: card.school.id });
        navigate(`/migration-v1-v2/${project.id}`);
      } catch {
        setError("Impossible d'ouvrir le projet de migration");
      }
    },
    [navigate]
  );

  const stats = board?.stats;

  const owners = useMemo(() => board?.owners ?? [], [board?.owners]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white shrink-0">
            <ArrowRightLeft className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-violet-600">
              Migration assistée
            </p>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Dossiers de migration
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
              Pour chaque école, lancez et vérifiez la migration <strong>module par module</strong>.
              La colonne « Bloquée » se remplit automatiquement en cas d'erreur ou de mise en pause.
            </p>
          </div>
        </div>

        {/* Header stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 shrink-0">
            <HeaderStat label="Dossiers" value={stats.total} color="text-gray-900 dark:text-gray-100" />
            <HeaderStat label="En cours" value={stats.inProgress} color="text-violet-600" />
            <HeaderStat label="Bloquées" value={stats.blocked} color="text-red-600" />
            <HeaderStat label="Terminées" value={stats.completed} color="text-emerald-600" />
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Rechercher une école, un code…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={ownerIdParam}
          onValueChange={(val) => updateParams({ ownerId: val })}
        >
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Tous responsables" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous responsables</SelectItem>
            {owners.map((o) => (
              <SelectItem key={o.id} value={String(o.id)}>
                {o.firstname} {o.lastname}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtre par date de migration prévue : jour ou semaine */}
        <Select
          value={planMode || 'none'}
          onValueChange={(val) => updateParams({ planMode: (val === 'none' ? '' : (val as PlanMode)) })}
        >
          <SelectTrigger className="w-44">
            <CalendarClock className="h-4 w-4 text-gray-400" />
            <SelectValue placeholder="Date prévue" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Toutes dates</SelectItem>
            <SelectItem value="day">Par jour</SelectItem>
            <SelectItem value="week">Par semaine</SelectItem>
          </SelectContent>
        </Select>
        {planMode && (
          <Input
            type="date"
            value={planDate || todayIso()}
            onChange={(e) => updateParams({ planDate: e.target.value })}
            className="w-44"
            aria-label={planMode === 'week' ? 'Semaine de migration prévue' : 'Jour de migration prévue'}
          />
        )}

        {hasActiveFilters && (
          <Button variant="ghost" onClick={resetFilters} className="text-gray-500">
            <RotateCcw className="h-4 w-4" />
            Réinitialiser
          </Button>
        )}
      </div>

      {/* Kanban grid */}
      {loading && !board ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMN_DEFS.map((col) => (
            <div key={col.key} className="space-y-3">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-28 rounded-lg" />
              <Skeleton className="h-28 rounded-lg" />
            </div>
          ))}
        </div>
      ) : board ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COLUMN_DEFS.map((col) => (
            <BoardColumn
              key={col.key}
              columnKey={col.key}
              title={col.title}
              cards={board.columns[col.key]}
              onCardClick={onCardClick}
              canOpenCard={canOpenCard}
              renderAssign={
                canManage
                  ? (card) => (
                      <AssignUserControl
                        projectId={card.project?.id ?? null}
                        schoolId={card.school.id}
                        assignedTo={card.project?.assignedTo ?? null}
                        users={assignableUsers}
                        onAssigned={() => fetchBoard()}
                        onError={(m) => setError(m)}
                        triggerClassName="h-7 text-xs"
                      />
                    )
                  : undefined
              }
              renderPlanned={
                canManage
                  ? (card) => (
                      <PlannedDateControl
                        projectId={card.project?.id ?? null}
                        schoolId={card.school.id}
                        plannedMigrationDate={card.project?.plannedMigrationDate ?? null}
                        onUpdated={() => fetchBoard()}
                        onError={(m) => setError(m)}
                        inputClassName="h-7 w-[8.5rem] text-xs"
                      />
                    )
                  : undefined
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
